import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function Header({ user, onLogout, onUpdateUser, onShowProfile }) {
  const [guestAvatar, setGuestAvatar] = useState(null);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    if (user?.username === 'Guest') {
      const candidates = [
        '/images/mascot/owl-idle.svg',
        '/images/mascot/owl-happy.svg'
      ];
      const pick = candidates[Math.floor(Math.random() * candidates.length)];
      setGuestAvatar(pick);
    } else {
      setGuestAvatar(null);
    }
  }, [user]);

  return (
    <header className="bg-white/80 backdrop-blur sticky top-0 z-30">
      <div className="container mx-auto p-2 sm:p-4 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3">
          <img 
            src="/images/logo/MathVenture_logo.png" 
            alt="MathVenture Logo" 
            className="h-8 sm:h-12 w-auto"
          />
          <div className="hidden sm:block">
            {/* Fancy typographic wordmark */}
            <div className="flex items-center gap-2">
              <span
                className="text-xl sm:text-2xl leading-none"
                style={{
                  fontFamily: 'Pacifico, Poppins, sans-serif',
                  background: 'linear-gradient(90deg,#7c3aed,#4f46e5)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  textShadow: '0 2px 6px rgba(79,70,229,0.14), 0 1px 0 rgba(0,0,0,0.04)'
                }}
              >
                MathVenture
              </span>
              <svg width="18" height="18" className="sm:w-[22px] sm:h-[22px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <defs>
                  <linearGradient id="g1" x1="0" x2="1">
                    <stop offset="0" stopColor="#f472b6" />
                    <stop offset="1" stopColor="#fb923c" />
                  </linearGradient>
                </defs>
                <circle cx="12" cy="12" r="10" fill="url(#g1)" />
                <path d="M7 13c1 1 2 2 5 2s4-1 5-2" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="text-xs text-gray-500">Playful math for grades 4 and 5</div>
          </div>
          {/* Mobile logo text */}
          <div className="sm:hidden">
            <span
              className="text-lg font-bold"
              style={{
                fontFamily: 'Pacifico, Poppins, sans-serif',
                background: 'linear-gradient(90deg,#7c3aed,#4f46e5)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              MathVenture
            </span>
          </div>
        </Link>
        <nav className="flex items-center gap-1 sm:gap-4">
          <Link to="/leaderboard" className="hidden sm:block text-sm hover:text-primary">Leaderboard</Link>
          <Link to="/games" className="hidden sm:block text-sm hover:text-primary">Games</Link>
          {user?.id === -1 && (
            <Link to="/pricing" className="hidden sm:block text-sm hover:text-primary text-purple-600 font-medium">Pricing</Link>
          )}
          
          {user?.id === -1 ? (
            // Guest user
            <div className="flex items-center gap-1 sm:gap-3">
              <Link to="/login" className="text-xs sm:text-sm hover:text-primary">Login</Link>
              <Link to="/register" className="px-2 py-1 sm:px-4 sm:py-2 rounded-lg bg-primary text-white text-xs sm:text-sm hover:bg-primary-dark">
                Sign Up
              </Link>
              {!avatarError && guestAvatar ? (
                <img
                  src={guestAvatar}
                  alt="Guest avatar"
                  className="w-10 h-10 rounded-full object-contain"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-medium">
                  G
                </div>
              )}
            </div>
          ) : (
            // Logged in user
            <div className="flex items-center gap-1 sm:gap-3">
              <Link to="/dashboard" className="hidden sm:block text-sm hover:text-primary">Dashboard</Link>
              
              {user.is_admin && (
                <Link to="/dashboard-selection" className="hidden sm:block text-sm text-purple-600 hover:text-purple-800 font-medium">
                  Admin
                </Link>
              )}
              
              <Link to="/subscription-manager" className="hidden sm:block text-sm hover:text-primary">
                Subscription
              </Link>
              
              <button
                onClick={() => onShowProfile && onShowProfile()}
                className="flex items-center gap-1 sm:gap-2 hover:bg-gray-100 rounded-lg p-1 sm:p-2 transition-colors"
                title="View Profile"
              >
                <img
                  src={`/images/avatars/person-${Math.random() < 0.5 ? 'idle' : 'happy'}.svg`}
                  alt="User avatar"
                  className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-contain"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="text-xs sm:text-sm">
                  <span className="hidden sm:inline">{user?.username ? (user.username.split('@')[0].charAt(0).toUpperCase() + user.username.split('@')[0].slice(1)) : 'User'}</span>
                  <span className="sm:hidden">{user?.username ? ((user.username.split('@')[0].charAt(0).toUpperCase() + user.username.split('@')[0].slice(1)).slice(0, 8) + ((user.username.split('@')[0].charAt(0).toUpperCase() + user.username.split('@')[0].slice(1)).length > 8 ? '...' : '')) : 'User'}</span>
                  {user.is_admin && <span className="ml-1 text-xs text-purple-600">👑</span>}
                </div>
              </button>
              
              <button
                onClick={onLogout}
                className="text-xs sm:text-sm text-red-600 hover:text-red-800 px-1 sm:px-2"
              >
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">Exit</span>
              </button>
            </div>
          )}
        </nav>
      </div>
      

    </header>
  );
}
